import { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import Breadcrumb from '../components/Breadcrumb'
import { blogCategories, blogPosts } from '../data/blog'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default function Blog() {
  const [activeFilter, setActiveFilter] = useState('All')
  const gridRef = useRef<HTMLDivElement>(null)

  const filteredPosts = activeFilter === 'All'
    ? blogPosts
    : blogPosts.filter(p => p.category === activeFilter)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cards = gridRef.current?.querySelectorAll('.blog-card')
      if (cards) {
        gsap.fromTo(
          cards,
          { y: 50, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: gridRef.current,
              start: 'top bottom',
              toggleActions: 'play none none none',
              invalidateOnRefresh: true,
            },
          }
        )
      }
    }, gridRef)

    return () => ctx.revert()
  }, [activeFilter])

  return (
    <div className="min-h-screen bg-[#050505] pb-20">
      <Helmet>
        <title>Blog — Technifire Software</title>
        <meta name="description" content="Insights on web development, SaaS, AI, e-commerce, CRM, branding, and SEO from the Technifire Software team. Expert guides and industry trends." />
        <link rel="canonical" href="https://technifire.io/blog" />
        <meta property="og:title" content="Blog — Technifire Software" />
        <meta property="og:description" content="Expert insights on web development, SaaS, AI, e-commerce, and digital growth strategies from Technifire." />
        <meta property="og:url" content="https://technifire.io/blog" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Breadcrumb current="BLOG" />

      {/* Hero */}
      <div className="px-6 lg:px-10 pt-8 pb-12">
        <h1 className="text-h2 uppercase text-white mb-10">BLOG</h1>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2">
          {blogCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`filter-pill ${activeFilter === cat ? 'active' : ''}`}
            >
              {cat.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Blog Grid */}
      <div ref={gridRef} className="px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post) => (
            <article
              key={post.id}
              className="blog-card card-hover rounded-2xl overflow-hidden bg-[#0a0a0a] group cursor-pointer"
            >
              <div className="relative overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Red gradient overlay from bottom to center */}
                <div className="absolute inset-0 bg-gradient-to-t from-red-600/80 via-red-600/20 to-transparent to-60%" />
                {/* Darker overlay on hover */}
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="absolute top-3 left-3 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs text-white z-10">
                  #{post.category.toUpperCase()}
                </span>
              </div>
              <div className="p-5">
                <time className="text-mono text-[#9A9A9A] mb-2 block">{post.date}</time>
                <h3 className="text-white font-medium mb-3 group-hover:text-[#9A9A9A] transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <span className="text-sm text-[#9A9A9A] group-hover:text-white transition-colors">
                  Read the article →
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  )
}
